import { map } from "bluebird";
import { value } from "~/components/helpers";
import { type AppContext } from "~/lib/context";
import { isNil, uniqBy } from "~/lib/lodash";
import { logWarn } from "~/lib/logger";

type LocationParams = {
  companyId: number;
  externalId: string | null;
  name: string | null;
  countryId?: number | null;
  countryCode?: string | null;
  mappedLocationId?: number | null;
};

export const buildLocationPayload = (params: LocationParams) => {
  const { companyId, externalId, name, countryId, countryCode, mappedLocationId } = params;

  if (!externalId || !name) {
    return null;
  }

  const country = value(() => {
    if (!countryCode && !countryId) {
      return null;
    }

    if (countryId) {
      return { country: { connect: { id: countryId } } };
    }

    if (countryCode) {
      return {
        country: {
          connect: { alpha2: countryCode },
        },
      };
    }
  });

  return {
    location: {
      connectOrCreate: {
        where: {
          companyId_externalId: {
            companyId,
            externalId,
          },
        },
        create: {
          externalId,
          name,
          autoMappingEnabled: true,
          company: {
            connect: { id: companyId },
          },
          ...country,
          ...(mappedLocationId && { mappedLocation: { connect: { id: mappedLocationId } } }),
        },
      },
    },
  };
};

export type LocationPayload = ReturnType<typeof buildLocationPayload>;

export const getLocationUpdatePayload = (params: LocationPayload | undefined) => {
  if (isNil(params?.location)) {
    return null;
  }

  const {
    location: {
      connectOrCreate: { create },
    },
  } = params;

  return {
    locations: true,
    companyId: create.company.connect.id,
    externalId: create.externalId,
    data: create,
  };
};

export const updateLocationsPostSync = async (ctx: AppContext, locations: NonNullable<LocationUpdatePayload>[]) => {
  const uniqueLocations = uniqBy(locations, (location) => location.companyId + location.externalId);

  return map(uniqueLocations, async (payload) => {
    const { companyId, externalId, data } = payload;

    const existingLocation = await ctx.prisma.externalLocation.findUnique({
      where: { companyId_externalId: { companyId, externalId } },
    });

    if (!existingLocation) {
      logWarn(ctx, "[updateLocationsPostSync] Location not found", {
        companyId,
        externalId,
      });
    }

    if (existingLocation) {
      return ctx.prisma.externalLocation.update({
        where: { id: existingLocation.id },
        data,
      });
    }
  });
};

export type LocationUpdatePayload = ReturnType<typeof getLocationUpdatePayload>;
